import React from 'react'
import clsx from 'clsx'
import { Clock, CloseCircle } from 'iconsax-react'
import { useTheme } from '@mui/material'

import Spacer from '../../../../../atoms/Spacer'
import NewCount from '../../../../BaseConversation/NewCount'
import TypingIndicator from '../../../../../atoms/TypingIndicator'
import Tooltip from '../../../../../atoms/Tooltip'

import {
  getShortenedDistance,
  findFilterID,
  getLatestEvent,
  formatLastMessage,
  loadTaggedUsers
} from '../../../../../../utilities'
import { useQueueState } from '../../../../../../providers/QueueStateProvider'
import { useReadStatus } from '../../../../../../providers/ReadStatusProvider'
import { useNotificationCount } from '../../../../../../providers/NotificationCountProvider'
import { useEnvironment } from '../../../../../../providers/EnvironmentProvider'
import { useUserAPI } from '../../../../../../providers/UserAPIProvider'
import { useTyping } from '../../../../../../providers/TypingProvider'
import { useAgent } from '../../../../../../providers/AgentProvider'
import { useSocket } from '../../../../../../providers/SocketProvider'
import { useAreYouSure } from '../../../../../../providers/AreYouSureProvider'
import { useBreakpoint } from '../../../../../../providers/BreakpointProvider'
import useInterval from '../../../../../../hooks/useInterval'
import useBaseStyles from '../../../../BaseConversation/Content/styles'
import useStyles from './styles'
import SmallButton from '../../../../../atoms/SmallButton'

const DELETED = 'Deleted Contact'

const Content = ({ name, conversation, hovering, modal, pickedUp }) => {
  const {
    id: queueSelected,
    handleClose,
    handleSelect,
    setAccepted
  } = useQueueState()
  const theme = useTheme()
  const { convos } = useReadStatus()
  const { updateCount } = useNotificationCount()
  const { user } = useEnvironment()
  const { getUsers, allUsers, loadUser } = useUserAPI()
  const { getTyping } = useTyping()
  const { currentUser } = useAgent()
  const {
    api: {
      denyConversation: denyConversationAPI,
      acceptConversation: acceptConversationAPI,
      endSessions,
      rejectConversation
    }
  } = useSocket()
  const { setAction, setReason, setSubtext, setTitle } = useAreYouSure()
  const { down767 } = useBreakpoint()

  const { session, messages, owner_id, users, isNew, ended_at } = conversation
  const isSelected = React.useMemo(
    () => session === queueSelected,
    [session, queueSelected]
  )

  const isYours = React.useMemo(() => users.includes(user?.id), [users, user])

  const allAgents = React.useMemo(
    () => Object.values(getUsers(false)),
    [getUsers]
  )

  const [timeSince, setTimeSince] = React.useState('')
  const [lastLoaded, setLastLoaded] = React.useState('')
  const nameRef = React.useRef()

  // Check last event, can be message | note | null
  const lastMessage = React.useMemo(() => {
    const filter = findFilterID(owner_id, false, session)
    const latest = getLatestEvent(conversation, filter)
    return latest
  }, [owner_id, session, conversation])

  // Updates time since every 5 seconds
  const updateTimeSince = React.useCallback(() => {
    setTimeSince(getShortenedDistance(lastMessage?.date))
  }, [lastMessage])
  useInterval(1000 * 5, updateTimeSince)

  // Gets list of typing people
  const typingNames = React.useMemo(
    () => getTyping(session),
    [getTyping, session]
  )

  // Gets number of new messages
  const newCount = React.useMemo(() => {
    if (isSelected) {
      return 0
    }

    return updateCount(session, convos, messages)
  }, [isSelected, updateCount, session, convos, messages])

  const denyConversation = React.useCallback(
    id => {
      handleClose(id, () => {
        denyConversationAPI(id)
      })
    },
    [handleClose, denyConversationAPI]
  )

  const acceptConversation = React.useCallback(
    async id => {
      await acceptConversationAPI(id)
      handleSelect(id)
    },
    [acceptConversationAPI, handleSelect]
  )
  const baseClasses = useBaseStyles()
  const classes = useStyles({ modal })

  const lastText = React.useMemo(
    () => formatLastMessage(lastMessage, allAgents, allUsers, currentUser),
    [lastMessage, allAgents, allUsers, currentUser]
  )

  // Loads all tagged users from a message
  React.useEffect(() => {
    if (
      !lastMessage ||
      lastMessage.content === lastLoaded ||
      typeof lastMessage.content !== 'string'
    ) {
      return
    }

    const { content = '' } = lastMessage
    loadTaggedUsers(content, loadUser)

    setLastLoaded(content)
  }, [lastMessage, lastLoaded, loadUser])

  const iconElem = React.useMemo(() => {
    const hideClose = (!isSelected && (isSelected || !hovering)) || modal

    if (hideClose) {
      return <Clock size={theme.size.base} className='icon' />
    }

    return (
      <CloseCircle
        size={theme.size.base}
        onClick={e => {
          e.stopPropagation()

          const action = () => {
            handleClose(session, () => {
              if (isYours) {
                endSessions([session], true)
              } else {
                rejectConversation(session)
              }
            })
          }

          if (isYours && !ended_at) {
            setReason('Closing this conversation will also end it.')
            setSubtext('Are you sure?')
            setTitle('ARE YOU SURE?')
            setAction(action)
          } else {
            action()
          }
        }}
        className='icon button'
      />
    )
  }, [
    isSelected,
    hovering,
    modal,
    theme.size.base,
    isYours,
    ended_at,
    handleClose,
    session,
    endSessions,
    rejectConversation,
    setReason,
    setSubtext,
    setTitle,
    setAction
  ])

  let nameElem = (
    <div
      ref={nameRef}
      className={clsx(
        baseClasses.name,
        user?.deleted_at && baseClasses.faded,
        classes.name,
        modal && !down767 && classes.modalName
      )}
    >
      {user?.deleted_at ? DELETED : name}
    </div>
  )

  // Show tooltip if name is overflowing
  if (nameRef.current) {
    const element = nameRef.current
    if (element.offsetWidth < element.scrollWidth) {
      nameElem = (
        <Tooltip
          title={user?.deleted_at ? DELETED : name}
          placement='right'
          arrow
        >
          {nameElem ?? 'Loading...'}
        </Tooltip>
      )
    }
  }

  return (
    <div className={clsx(baseClasses.root, classes.root)}>
      <div className={clsx(baseClasses.contentBlock, classes.contentBlock)}>
        <div className={clsx(baseClasses.title, classes.title)}>
          {nameElem}

          <Spacer />

          <div className={baseClasses.time}>{timeSince}</div>
        </div>
        {!pickedUp && (
          <div className={classes.smallButtons}>
            <SmallButton
              onClick={() => {
                denyConversation(session)
              }}
            >
              Reject
            </SmallButton>{' '}
            <SmallButton
              onClick={() => {
                setAccepted(true)
                acceptConversation(session)
              }}
              positive
            >
              Accept
            </SmallButton>
          </div>
        )}
        {pickedUp && (
          <div
            className={clsx(
              baseClasses.content,
              classes.content,
              modal && !down767 && classes.modalContent
            )}
          >
            {typingNames.length > 0 ? (
              <TypingIndicator names={typingNames} numUsers={users.length} />
            ) : (
              lastText
            )}
          </div>
        )}
      </div>

      <div className={clsx(baseClasses.controlsBlock, classes.controlsBlock)}>
        {pickedUp && iconElem}

        {lastMessage && lastMessage.senderFull?.id !== user?.id ? (
          isNew || newCount ? (
            <NewCount count={newCount} />
          ) : (
            <div />
          )
        ) : (
          <div />
        )}
      </div>
    </div>
  )
}

export default Content
